import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import DynamicButton from "../DynamicButton/DynamicButton";
import "./TableColumnFilters.css";

const TIME_MINUTES = ["00", "15", "30", "45"];

/* ============================================================
   GENERAL HELPERS
   ============================================================ */

const isEnabled = (value) =>
  value === true ||
  value === 1 ||
  String(value).trim().toLowerCase() === "true" ||
  String(value).trim() === "1";

const hasValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return String(value ?? "").trim() !== "";
};

const getColumnType = (column) =>
  String(column?.type || "text")
    .trim()
    .toLowerCase();

const cloneFilters = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      Array.isArray(value) ? [...value] : value,
    ]),
  );

const getMultiValues = (value) => {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const isMultiSelectColumn = (column) =>
  isEnabled(column?.multiple) ||
  isEnabled(column?.multiSelect) ||
  isEnabled(column?.array);

const arraysEqual = (first = [], second = []) => {
  if (first.length !== second.length) {
    return false;
  }

  const firstSorted = [...first].map(String).sort();
  const secondSorted = [...second].map(String).sort();

  return firstSorted.every((value, index) => value === secondSorted[index]);
};

/* ============================================================
   DATE HELPERS
   ============================================================ */

const toDateInputValue = (value) => {
  if (!value) return "";

  const stringValue = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  const isoMatch = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);

  if (isoMatch) {
    return isoMatch[1];
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* ============================================================
   TIME HELPERS
   ============================================================ */

const parseTime12 = (value) => {
  const match = String(value || "").match(
    /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i,
  );

  if (!match) {
    return {
      hour: "",
      minute: "",
      period: "",
    };
  }

  return {
    hour: String(Number(match[1])),
    minute: match[2],
    period: match[3].toUpperCase(),
  };
};

const formatTime12 = (hour, minute, period) => {
  if (!hour || !minute || !period) {
    return "";
  }

  const normalizedHour = String(((Number(hour) - 1 + 12) % 12) + 1);

  const normalizedMinute = String(minute).padStart(2, "0");

  const normalizedPeriod = String(period).toUpperCase() === "PM" ? "PM" : "AM";

  return `${normalizedHour}:${normalizedMinute} ${normalizedPeriod}`;
};

/* ============================================================
   OPTION HELPERS
   ============================================================ */

const normalizeStaticOption = (option, index) => {
  if (
    typeof option === "string" ||
    typeof option === "number" ||
    typeof option === "boolean"
  ) {
    return {
      key: `${String(option)}-${index}`,
      label: String(option),
      value: String(option),
    };
  }

  const value =
    option?.value ??
    option?._id ??
    option?.id ??
    option?.name ??
    option?.label ??
    "";

  const label =
    option?.label ??
    option?.name ??
    option?.title ??
    option?.projectName ??
    option?.roleName ??
    value;

  return {
    key: String(option?._id || option?.id || value || index),
    label: String(label),
    value: String(value),
  };
};

const getRemoteOptionValue = (column, option) => {
  if (
    column?.filterValueKey === "_id" ||
    column?.filterValueKey === "id" ||
    column?.filterBy === "id" ||
    column?.filterBy === "_id"
  ) {
    return String(option.value);
  }

  return String(option.label);
};

/* ============================================================
   ACTIVE FILTER HELPERS
   ============================================================ */

const isColumnActive = (column, filters = {}) => {
  const field = column?.field;
  const type = getColumnType(column);

  if (!field) {
    return false;
  }

  if (type === "date") {
    return (
      hasValue(filters[`${field}__from`]) || hasValue(filters[`${field}__to`])
    );
  }

  return hasValue(filters[field]);
};

/* ============================================================
   COMPONENT
   ============================================================ */

const TableColumnFilters = ({
  columns = [],
  columnFilters = {},
  setColumnFilters,
  setCurrentPage,
}) => {
  const [open, setOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");

  /*
    draftFilters:
    Every input updates this state.

    columnFilters:
    The actual table filter state.

    columnFilters is updated only when Done is clicked.
  */
  const [draftFilters, setDraftFilters] = useState({});

  const [optionsCache, setOptionsCache] = useState({});
  const [loadingFields, setLoadingFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  /* ============================================================
     FILTERABLE COLUMNS
     ============================================================ */

  const filterableColumns = useMemo(() => {
    return columns.filter(
      (column) => column?.field && isEnabled(column?.filter),
    );
  }, [columns]);

  const visibleColumns = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();

    if (!query) {
      return filterableColumns;
    }

    return filterableColumns.filter((column) => {
      const searchableText = [
        column?.label,
        column?.field,
        column?.placeholder,
        column?.filterPlaceholder,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [fieldSearch, filterableColumns]);

  /* ============================================================
     APPLIED AND DRAFT COUNTS
     ============================================================ */

  const appliedActiveColumns = useMemo(() => {
    return filterableColumns.filter((column) =>
      isColumnActive(column, columnFilters),
    );
  }, [columnFilters, filterableColumns]);

  const draftActiveColumns = useMemo(() => {
    return filterableColumns.filter((column) =>
      isColumnActive(column, draftFilters),
    );
  }, [draftFilters, filterableColumns]);

  const appliedFilterCount = appliedActiveColumns.length;
  const draftFilterCount = draftActiveColumns.length;

  const hasDraftChanges = useMemo(() => {
    return (
      JSON.stringify(cloneFilters(draftFilters)) !==
      JSON.stringify(cloneFilters(columnFilters))
    );
  }, [columnFilters, draftFilters]);

  /* ============================================================
     PAGE RESET
     ============================================================ */

  const resetPage = useCallback(() => {
    setCurrentPage?.(1);
  }, [setCurrentPage]);

  /* ============================================================
     DRAFT FILTER HANDLERS
     ============================================================ */

  const setDraftFilterValue = useCallback((field, value) => {
    if (!field) return;

    setDraftFilters((previousFilters = {}) => {
      const nextFilters = { ...previousFilters };

      if (hasValue(value)) {
        nextFilters[field] = value;
      } else {
        delete nextFilters[field];
      }

      return nextFilters;
    });
  }, []);

  const clearDraftColumn = useCallback((column) => {
    const field = column?.field;
    const type = getColumnType(column);

    if (!field) return;

    setDraftFilters((previousFilters = {}) => {
      const nextFilters = { ...previousFilters };

      if (type === "date") {
        delete nextFilters[`${field}__from`];
        delete nextFilters[`${field}__to`];
      } else {
        delete nextFilters[field];
      }

      return nextFilters;
    });
  }, []);

  const clearAllDraftFilters = useCallback(() => {
    setDraftFilters({});
  }, []);

  /* ============================================================
     OPEN, CANCEL AND APPLY
     ============================================================ */

  const openFilters = useCallback(() => {
    setDraftFilters(cloneFilters(columnFilters));
    setFieldSearch("");
    setOpen(true);
  }, [columnFilters]);

  const closeFilters = useCallback(() => {
    /*
      Closing without Done discards draft changes.
    */
    setDraftFilters(cloneFilters(columnFilters));
    setFieldSearch("");
    setOpen(false);
  }, [columnFilters]);

  const applyFilters = useCallback(() => {
    /*
      This is the only place where the real table filters change.
    */
    setColumnFilters(cloneFilters(draftFilters));
    resetPage();
    setFieldSearch("");
    setOpen(false);
  }, [draftFilters, resetPage, setColumnFilters]);

  /* ============================================================
     REMOTE OPTION LOADING
     ============================================================ */

  const loadColumnOptions = useCallback(async (column) => {
    const field = column?.field;
    const api = column?.api;
    const apiTitle = column?.apiTitle;

    if (!field || !api || !apiTitle) {
      return;
    }

    setLoadingFields((previous) => ({
      ...previous,
      [field]: true,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [field]: "",
    }));

    try {
      const response = await fetch(api);

      if (!response.ok) {
        throw new Error(`Unable to load options (${response.status})`);
      }

      const responseData = await response.json();

      const records = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.results)
            ? responseData.results
            : Array.isArray(responseData?.items)
              ? responseData.items
              : [];

      const options = records
        .map((item, index) => {
          const label =
            item?.[apiTitle] ??
            item?.label ??
            item?.name ??
            item?.title ??
            item?.projectName ??
            item?.roleName;

          const value = item?._id ?? item?.id ?? item?.value ?? label;

          if (
            label === undefined ||
            label === null ||
            String(label).trim() === ""
          ) {
            return null;
          }

          return {
            key: String(value || `${field}-${index}`),
            label: String(label),
            value: String(value ?? label),
          };
        })
        .filter(Boolean);

      setOptionsCache((previous) => ({
        ...previous,
        [field]: options,
      }));
    } catch (error) {
      setFieldErrors((previous) => ({
        ...previous,
        [field]: error?.message || "Unable to load options",
      }));
    } finally {
      setLoadingFields((previous) => ({
        ...previous,
        [field]: false,
      }));
    }
  }, []);

  useEffect(() => {
    filterableColumns.forEach((column) => {
      const field = column.field;

      const requiresRemoteOptions = column?.api && column?.apiTitle;

      const hasLoaded = Object.prototype.hasOwnProperty.call(
        optionsCache,
        field,
      );

      const isLoading = Boolean(loadingFields[field]);
      const hasError = Boolean(fieldErrors[field]);

      if (requiresRemoteOptions && !hasLoaded && !isLoading && !hasError) {
        loadColumnOptions(column);
      }
    });
  }, [
    fieldErrors,
    filterableColumns,
    loadColumnOptions,
    loadingFields,
    optionsCache,
  ]);

  /* ============================================================
     OUTSIDE CLICK AND ESCAPE
     ============================================================ */

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeFilters();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeFilters();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    document.addEventListener("keydown", handleEscape);

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);

      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeFilters, open]);

  /* ============================================================
     ERROR UI
     ============================================================ */

  const renderError = (field, column) => {
    const error = fieldErrors[field];

    if (!error) {
      return null;
    }

    return (
      <div className="safety-column-filter__error">
        <span>{error}</span>

        <button type="button" onClick={() => loadColumnOptions(column)}>
          Retry
        </button>
      </div>
    );
  };

  /* ============================================================
     MULTISELECT
     ============================================================ */

  const renderMultiSelect = ({
    column,
    options,
    loading = false,
    error = "",
  }) => {
    const field = column.field;
    const selectedValues = getMultiValues(draftFilters[field]);

    const normalizedOptions = options.map((option) => ({
      ...option,
      filterValue: String(option.filterValue ?? option.value),
    }));

    const allValues = normalizedOptions.map((option) => option.filterValue);

    const allSelected =
      allValues.length > 0 && arraysEqual(selectedValues, allValues);

    const toggleOption = (optionValue, checked) => {
      const nextValues = checked
        ? Array.from(new Set([...selectedValues, optionValue]))
        : selectedValues.filter((value) => value !== optionValue);

      /*
        Keep comma-separated output for compatibility with
        the existing table filtering logic.
      */
      setDraftFilterValue(field, nextValues.join(", "));
    };

    const selectAll = () => {
      setDraftFilterValue(field, allValues.join(", "));
    };

    const clearSelection = () => {
      setDraftFilterValue(field, "");
    };

    return (
      <>
        <div className="safety-column-filter__multi">
          <div className="safety-column-filter__multi-header">
            <span>
              {selectedValues.length
                ? `${selectedValues.length} selected`
                : "Select one or more"}
            </span>

            <div className="safety-column-filter__multi-actions">
              {!allSelected && allValues.length > 0 && (
                <button type="button" onClick={selectAll}>
                  Select all
                </button>
              )}

              {selectedValues.length > 0 && (
                <button type="button" onClick={clearSelection}>
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="safety-column-filter__multi-options">
            {loading ? (
              <div className="safety-column-filter__multi-state">
                Loading options...
              </div>
            ) : error ? (
              <div className="safety-column-filter__multi-state">
                Options could not be loaded.
              </div>
            ) : normalizedOptions.length === 0 ? (
              <div className="safety-column-filter__multi-state">
                No options available.
              </div>
            ) : (
              normalizedOptions.map((option) => {
                const checked = selectedValues.includes(option.filterValue);

                return (
                  <label
                    key={option.key}
                    className={`safety-column-filter__multi-option ${
                      checked
                        ? "safety-column-filter__multi-option--selected"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) =>
                        toggleOption(option.filterValue, event.target.checked)
                      }
                    />

                    <span>{option.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {renderError(field, column)}
      </>
    );
  };

  /* ============================================================
     DATE FILTER
     ============================================================ */

  const renderDateFilter = (column) => {
    const field = column.field;
    const fromKey = `${field}__from`;
    const toKey = `${field}__to`;

    const fromValue = toDateInputValue(draftFilters[fromKey]);

    const toValue = toDateInputValue(draftFilters[toKey]);

    return (
      <div className="safety-column-filter__date-grid">
        <label className="safety-column-filter__sub-field">
          <span>From</span>

          <input
            type="date"
            className="safety-column-filter__input"
            value={fromValue}
            max={toValue || undefined}
            onChange={(event) =>
              setDraftFilterValue(fromKey, toDateInputValue(event.target.value))
            }
          />
        </label>

        <label className="safety-column-filter__sub-field">
          <span>To</span>

          <input
            type="date"
            className="safety-column-filter__input"
            value={toValue}
            min={fromValue || undefined}
            onChange={(event) =>
              setDraftFilterValue(toKey, toDateInputValue(event.target.value))
            }
          />
        </label>
      </div>
    );
  };

  /* ============================================================
     TIME FILTER
     ============================================================ */

  const renderTimeFilter = (column) => {
    const field = column.field;

    const currentValue = draftFilters[field] ?? "";

    const { hour, minute, period } = parseTime12(currentValue);

    const updateTime = (nextValues) => {
      const nextHour = nextValues.hour ?? hour;
      const nextMinute = nextValues.minute ?? minute;
      const nextPeriod = nextValues.period ?? period;

      if (!nextHour && !nextMinute && !nextPeriod) {
        setDraftFilterValue(field, "");
        return;
      }

      setDraftFilterValue(
        field,
        formatTime12(nextHour || "1", nextMinute || "00", nextPeriod || "AM"),
      );
    };

    return (
      <div className="safety-column-filter__time-grid">
        <select
          className="safety-column-filter__input"
          value={hour}
          onChange={(event) =>
            updateTime({
              hour: event.target.value,
            })
          }
        >
          <option value="">Hour</option>

          {Array.from({ length: 12 }, (_, index) => {
            const value = String(index + 1);

            return (
              <option key={value} value={value}>
                {value}
              </option>
            );
          })}
        </select>

        <select
          className="safety-column-filter__input"
          value={minute}
          onChange={(event) =>
            updateTime({
              minute: event.target.value,
            })
          }
        >
          <option value="">Minute</option>

          {TIME_MINUTES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <select
          className="safety-column-filter__input"
          value={period}
          onChange={(event) =>
            updateTime({
              period: event.target.value,
            })
          }
        >
          <option value="">AM/PM</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    );
  };

  /* ============================================================
     STATIC DROPDOWN
     ============================================================ */

  const renderStaticDropdown = (column) => {
    const field = column.field;
    const value = draftFilters[field] ?? "";

    const options = (column.options || []).map(normalizeStaticOption);

    if (isMultiSelectColumn(column)) {
      return renderMultiSelect({
        column,
        options: options.map((option) => ({
          ...option,
          filterValue: option.value,
        })),
      });
    }

    return (
      <select
        className="safety-column-filter__input"
        value={value}
        onChange={(event) => setDraftFilterValue(field, event.target.value)}
      >
        <option value="">{column.allLabel || "All"}</option>

        {options.map((option) => (
          <option key={option.key} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  /* ============================================================
     REMOTE DROPDOWN
     ============================================================ */

  const renderRemoteDropdown = (column, forceMultiple = false) => {
    const field = column.field;
    const value = draftFilters[field] ?? "";

    const options = optionsCache[field] || [];
    const loading = Boolean(loadingFields[field]);
    const error = fieldErrors[field];

    const preparedOptions = options.map((option) => ({
      ...option,
      filterValue: getRemoteOptionValue(column, option),
    }));

    if (forceMultiple || isMultiSelectColumn(column)) {
      return renderMultiSelect({
        column,
        options: preparedOptions,
        loading,
        error,
      });
    }

    return (
      <>
        <select
          className="safety-column-filter__input"
          value={value}
          disabled={loading || Boolean(error)}
          onChange={(event) => setDraftFilterValue(field, event.target.value)}
        >
          <option value="">
            {loading ? "Loading options..." : column.allLabel || "All"}
          </option>

          {!loading &&
            !error &&
            preparedOptions.map((option) => (
              <option key={option.key} value={option.filterValue}>
                {option.label}
              </option>
            ))}
        </select>

        {renderError(field, column)}
      </>
    );
  };

  /* ============================================================
     FILTER CONTROL ROUTER
     ============================================================ */

  const renderFilterControl = (column) => {
    const field = column.field;
    const type = getColumnType(column);
    const value = draftFilters[field] ?? "";

    if (type === "date") {
      return renderDateFilter(column);
    }

    if (type === "time") {
      return renderTimeFilter(column);
    }

    if (type === "boolean") {
      return (
        <select
          className="safety-column-filter__input"
          value={value}
          onChange={(event) => setDraftFilterValue(field, event.target.value)}
        >
          <option value="">All</option>

          <option value="true">{column.trueLabel || "Yes"}</option>

          <option value="false">{column.falseLabel || "No"}</option>
        </select>
      );
    }

    if (
      (type === "dropdown" || type === "select") &&
      Array.isArray(column.options) &&
      !column.api
    ) {
      return renderStaticDropdown(column);
    }

    if (
      (type === "dropdown" || type === "select") &&
      column.api &&
      column.apiTitle
    ) {
      return renderRemoteDropdown(column);
    }

    if (type === "checkbox" && Array.isArray(column.options)) {
      return renderMultiSelect({
        column,
        options: column.options.map(normalizeStaticOption).map((option) => ({
          ...option,
          filterValue: option.value,
        })),
      });
    }

    if (type === "checkbox" && column.api && column.apiTitle) {
      return renderRemoteDropdown(column, true);
    }

    return (
      <input
        type={type === "number" ? "number" : "text"}
        className="safety-column-filter__input"
        placeholder={
          column.filterPlaceholder || `Filter by ${column.label || field}`
        }
        value={value}
        onChange={(event) => setDraftFilterValue(field, event.target.value)}
      />
    );
  };

  if (!filterableColumns.length) {
    return null;
  }

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div ref={rootRef} className="safety-column-filter">
      <DynamicButton
        text={
          appliedFilterCount > 0 ? `Filters (${appliedFilterCount})` : "Filters"
        }
        icon="fa-filter"
        size="sm"
        variant={appliedFilterCount > 0 ? "primary" : "outline"}
        onClick={() => {
          if (open) {
            closeFilters();
          } else {
            openFilters();
          }
        }}
        title={open ? "Close filters" : "Open filters"}
        aria-expanded={open}
      />

      {open && (
        <section
          className="safety-column-filter__popover"
          role="dialog"
          aria-label="Table filters"
        >
          {/* Header */}
          <header className="safety-column-filter__header">
            <div className="safety-column-filter__heading">
              <div className="safety-column-filter__heading-icon">
                <i className="fa fa-filter" aria-hidden="true" />
              </div>

              <div>
                <h3>Filter records</h3>

                <p>Select filters and click Done to apply them.</p>
              </div>
            </div>

            <button
              type="button"
              className="safety-column-filter__close"
              onClick={closeFilters}
              aria-label="Close without applying filters"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </header>

          {/* Search */}
          <div className="safety-column-filter__toolbar">
            <div className="safety-column-filter__search">
              <i className="fa fa-search" aria-hidden="true" />

              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search filter fields..."
                value={fieldSearch}
                onChange={(event) => setFieldSearch(event.target.value)}
              />

              {fieldSearch && (
                <button
                  type="button"
                  onClick={() => setFieldSearch("")}
                  aria-label="Clear field search"
                >
                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="safety-column-filter__count">
              {visibleColumns.length} of {filterableColumns.length} fields
            </div>
          </div>

          {/* Draft filter chips */}
          {draftActiveColumns.length > 0 && (
            <div className="safety-column-filter__active-list">
              <span className="safety-column-filter__active-title">
                Selected:
              </span>

              {draftActiveColumns.map((column) => (
                <button
                  key={column.field}
                  type="button"
                  className="safety-column-filter__active-chip"
                  onClick={() => clearDraftColumn(column)}
                  title={`Clear ${column.label || column.field}`}
                >
                  <span>{column.label || column.field}</span>

                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}

          {/* Filter fields */}
          <div className="safety-column-filter__body">
            {visibleColumns.length > 0 ? (
              <div className="safety-column-filter__grid">
                {visibleColumns.map((column) => {
                  const active = isColumnActive(column, draftFilters);

                  return (
                    <div
                      key={column.field}
                      className={`safety-column-filter__field ${
                        active ? "safety-column-filter__field--active" : ""
                      }`}
                    >
                      <div className="safety-column-filter__field-header">
                        <span className="safety-column-filter__label">
                          {column.label || column.field}
                        </span>

                        {active && (
                          <button
                            type="button"
                            className="safety-column-filter__clear"
                            onClick={() => clearDraftColumn(column)}
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      <div className="safety-column-filter__control">
                        {renderFilterControl(column)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="safety-column-filter__empty">
                <div className="safety-column-filter__empty-icon">
                  <i className="fa fa-search" aria-hidden="true" />
                </div>

                <strong>No fields found</strong>

                <p>Try searching with a different field name.</p>

                <button type="button" onClick={() => setFieldSearch("")}>
                  Clear search
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="safety-column-filter__footer">
            <div className="safety-column-filter__footer-summary">
              {draftFilterCount > 0 ? (
                <>
                  <strong>{draftFilterCount}</strong> selected{" "}
                  {draftFilterCount === 1 ? "filter" : "filters"}
                  {hasDraftChanges && (
                    <span className="safety-column-filter__pending">
                      {" "}
                      · Not applied
                    </span>
                  )}
                </>
              ) : (
                "No filters selected"
              )}
            </div>

            <div className="safety-column-filter__footer-actions">
              {draftFilterCount > 0 && (
                <DynamicButton
                  text="Clear all"
                  icon="fa-broom"
                  size="sm"
                  variant="outline"
                  onClick={clearAllDraftFilters}
                />
              )}

              <DynamicButton
                text="Cancel"
                icon="fa-times"
                size="sm"
                variant="secondary"
                onClick={closeFilters}
              />

              <DynamicButton
                text="Done"
                icon="fa-check"
                size="sm"
                variant="primary"
                onClick={applyFilters}
              />
            </div>
          </footer>
        </section>
      )}
    </div>
  );
};

export default TableColumnFilters;
